import { ReactNode } from 'react';
import { Field, FieldState } from '@enterprise/common';
import { SpotSelectBox, SpotSelectOption, SpotTextInput } from '@enterprise/spot';
import { observer } from 'mobx-react';
import { useStores } from '../../../../../hooks';
import { notEmpty } from '../../../../../core/helpers';
import { isAlphanumeric } from 'validator';
import { Trans } from 'react-i18next';
import { LookupItemTypes } from '../../../../../core/models/datasource/lookups';
import i18n from 'i18next';

interface HeaderControlProps {
    areFieldsDisabled?: boolean;
    queryPractice?: string;
    copyDescriptionToAll?: (description: string) => void;
    isNotExist?: (value: string, meta: FieldState<unknown> | undefined, message: string) => ReactNode | Promise<ReactNode | undefined>;
}

export const HeaderControl = observer(function HeaderControl(props: HeaderControlProps) {
    const {
        domain: { itemsPageStore: store },
    } = useStores();

    const { areFieldsDisabled, queryPractice, copyDescriptionToAll, isNotExist } = props;
    const itemTypes: LookupItemTypes[] = store.getLookupResults(store.itemTypes);
    const { isCreatingNewItem } = store;
    const pimsIdPlaceholder = i18n.t('controlCenter:itemFields.itemNumber', 'Item Number');
    const itemTypePlaceholder = i18n.t('common:selectValue', 'Select a value');
    const hospitalDescriptionPlaceholder = i18n.t('controlCenter:itemFields.hospitalDescription', 'HospitalDescription');
    const itemNumberAlreadyInUse = i18n.t('validation:itemNumberAlreadyInUse', 'Item Number already in use');

    const toUpperCase = (value) => {
        return value ? value.toUpperCase() : value;
    };

    return (
        <>
            <Field
                name="pimsId"
                parse={(value) => {
                    if (!value) {
                        return null;
                    }

                    return isCreatingNewItem ? toUpperCase(value) : value;
                }}
                validate={(val, _, meta) =>
                    notEmpty('Item Number')(val) ||
                    (!isAlphanumeric(val) && 'Item Number must contain only letters and numbers') ||
                    (val.length > 7 && 'Item Number should be between 1 and 7 characters in length') ||
                    (isCreatingNewItem && isNotExist?.(val, meta, itemNumberAlreadyInUse))
                }
            >
                {({ input, meta }) => <SpotTextInput {...input} error={meta} placeholder={pimsIdPlaceholder} />}
            </Field>
            <Field name="itemType.id" parse={(value) => value && Number(value)} validate={notEmpty('Item Type')}>
                {({ input, meta }) => (
                    <SpotSelectBox {...input} error={meta} placeholder={itemTypePlaceholder}>
                        {itemTypes.map((type) => (
                            <SpotSelectOption key={type.id} value={type.id}>
                                {type.description}
                            </SpotSelectOption>
                        ))}
                    </SpotSelectBox>
                )}
            </Field>
            <Field
                name="hospitalDescription"
                validate={(val, _) =>
                    notEmpty('Hospital Description')(val) ||
                    (!new RegExp('^[a-zA-Z0-9].*').test(val) &&
                        'The first character of the hospital description must be one of the following characters. 0-9 or A-Z or a-z') ||
                    (val.length > 40 && 'Hospital Description should be between 1 and 40 characters in length')
                }
            >
                {({ input, meta }) => {
                    const isCopyDescriptionLinkVisible =
                        !areFieldsDisabled && input.value && !(meta && meta.error && meta.touched) && !Boolean(queryPractice);
                    const copyHandler = () => copyDescriptionToAll?.(input.value);
                    return (
                        <div>
                            <SpotTextInput {...input} error={meta} placeholder={hospitalDescriptionPlaceholder} />
                            {isCopyDescriptionLinkVisible && (
                                <span className="spot-link copy-description-link" onClick={copyHandler}>
                                    <Trans i18nKey="common:item.applyDescriptionToAllVariants" />
                                </span>
                            )}
                        </div>
                    );
                }}
            </Field>
        </>
    );
});
